import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { TermsAcceptanceModal } from '../components/TermsAcceptanceModal';
import logoImg from '../assets/logo.png';

const providers = [
  { name: 'OpenAI', abbr: 'OAI', gradient: 'from-gray-700 to-gray-900 dark:from-gray-300 dark:to-gray-100' },
  { name: 'Anthropic', abbr: 'ANT', gradient: 'from-orange-500 to-amber-600' },
  { name: 'Google', abbr: 'GEM', gradient: 'from-blue-500 to-indigo-600' },
  { name: 'Mistral', abbr: 'MIS', gradient: 'from-purple-500 to-violet-600' },
  { name: 'DeepSeek', abbr: 'DSK', gradient: 'from-cyan-500 to-teal-600' },
  { name: 'Llama / Groq', abbr: 'LMA', gradient: 'from-green-500 to-emerald-600' },
];

const features = [
  {
    title: 'Multi-Model Access',
    description: 'Chat with GPT-4, Claude, Gemini, Mistral, DeepSeek, and Llama from a single interface.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
      </svg>
    ),
    color: 'from-blue-500 to-cyan-500',
  },
  {
    title: 'Real-Time Streaming',
    description: 'See AI responses appear token by token in real time with WebSocket-powered streaming.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
    color: 'from-amber-500 to-orange-500',
  },
  {
    title: 'Model Comparison',
    description: 'Run two models side-by-side on the same prompt and compare their outputs instantly.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
      </svg>
    ),
    color: 'from-purple-500 to-pink-500',
  },
  {
    title: 'File Attachments',
    description: 'Upload documents and files to provide context for your AI conversations.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
      </svg>
    ),
    color: 'from-green-500 to-emerald-500',
  },
  {
    title: 'Conversation Management',
    description: 'Organize chats into folders, search across conversations, and export your data.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
      </svg>
    ),
    color: 'from-indigo-500 to-blue-500',
  },
  {
    title: 'Enterprise Security',
    description: 'AES-256 encryption, audit logging, rate limiting, and role-based access control.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
    color: 'from-rose-500 to-red-500',
  },
];

// 3D tilt card component
function TiltCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -8;
    const rotateY = ((x - centerX) / centerX) * 8;
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
  }, []);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`transition-transform duration-200 ease-out ${className}`}
      style={{ transformStyle: 'preserve-3d' }}
    >
      {children}
    </div>
  );
}

// Animated provider showcase — staggered floating cards
function HeroVisual() {
  // Positions for a nice scattered layout (x%, y%, delay, duration)
  const positions = [
    { x: 10, y: 8, delay: 0, dur: 6 },
    { x: 55, y: 2, delay: 1, dur: 7 },
    { x: 30, y: 38, delay: 0.5, dur: 5.5 },
    { x: 70, y: 35, delay: 1.5, dur: 6.5 },
    { x: 12, y: 68, delay: 0.8, dur: 7.5 },
    { x: 58, y: 65, delay: 2, dur: 6 },
  ];

  return (
    <div className="w-full max-w-lg mx-auto mt-12 lg:mt-0">
      <div className="relative w-full" style={{ paddingBottom: '100%' }}>
        {/* Ambient glow */}
        <div className="absolute inset-12 rounded-full bg-gradient-to-br from-blue-500/15 via-purple-500/10 to-cyan-500/15 dark:from-blue-500/10 dark:via-purple-500/8 dark:to-cyan-500/10 blur-3xl animate-glow-pulse" />

        {/* Provider cards */}
        {providers.map((provider, i) => {
          const pos = positions[i];
          return (
            <div
              key={provider.name}
              className="absolute animate-float"
              style={{
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                animationDelay: `${pos.delay}s`,
                animationDuration: `${pos.dur}s`,
              }}
            >
              <div className="glass shadow-3d rounded-xl px-5 py-3 flex items-center gap-3 whitespace-nowrap hover:scale-105 hover:shadow-3d-lg transition-all duration-300 cursor-default">
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${provider.gradient} flex items-center justify-center shadow-md`}>
                  <span className="text-[10px] font-bold text-white">{provider.abbr}</span>
                </div>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{provider.name}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    // Only redirect to chat if authenticated AND terms accepted
    if (isAuthenticated && user?.termsAccepted) {
      navigate('/chat');
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 overflow-x-hidden">
      {/* Terms acceptance modal for first-time users */}
      {isAuthenticated && user && !user.termsAccepted && (
        <TermsAcceptanceModal onAccepted={() => navigate('/chat')} />
      )}

      {/* Background gradient orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-blue-400/20 to-cyan-400/10 dark:from-blue-600/10 dark:to-cyan-600/5 blur-3xl"
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />
        <div
          className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-purple-400/15 to-pink-400/10 dark:from-purple-600/8 dark:to-pink-600/5 blur-3xl"
          style={{ transform: `translateY(${scrollY * 0.15}px)` }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-emerald-400/10 to-teal-400/10 dark:from-emerald-600/5 dark:to-teal-600/5 blur-3xl"
          style={{ transform: `translateY(${scrollY * -0.08}px)` }}
        />
      </div>

      {/* Navigation - glassmorphism */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/20 dark:border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={logoImg} alt="Logo" className="w-8 h-8 drop-shadow-lg" />
              <span className="text-lg font-semibold text-gray-900 dark:text-white">Unified LLM</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/docs" className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                Documentation
              </Link>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-white/10 transition-all"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                  </svg>
                )}
              </button>
              <Link
                to="/login"
                className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-xl transition-all glow-blue"
              >
                Try Now
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-16 sm:pt-36 sm:pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="max-w-xl relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full glass shadow-3d">
                <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 animate-pulse" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Unified AI Platform</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                One Interface,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-500 to-cyan-500 animate-gradient">
                  Every AI Model
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                Access OpenAI, Anthropic, Google, Mistral, DeepSeek, and open-source models through a single,
                secure platform. Whether you're an individual, a business, or a large organization &mdash; compare
                models, stream responses in real time, and manage all your AI conversations in one place.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
                <Link
                  to="/login"
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-base font-semibold rounded-2xl transition-all glow-blue shadow-3d"
                >
                  Try Now
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
                <Link
                  to="/docs"
                  className="inline-flex items-center gap-2 px-8 py-4 glass shadow-3d text-gray-700 dark:text-gray-200 text-base font-semibold rounded-2xl hover:bg-white/80 dark:hover:bg-white/10 transition-all"
                >
                  Read the Docs
                </Link>
              </div>

              {/* Stats with 3D cards */}
              <div className="mt-12 grid grid-cols-3 gap-4">
                {[
                  { value: '6+', label: 'AI Providers' },
                  { value: '20+', label: 'Models' },
                  { value: 'AES-256', label: 'Encryption' },
                ].map((stat) => (
                  <div key={stat.label} className="glass rounded-xl p-4 shadow-3d text-center">
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: 3D floating providers */}
            <div className="hidden lg:block">
              <HeroVisual />
            </div>
          </div>
        </div>
      </section>

      {/* Providers bar (mobile-visible, desktop shows cube instead) */}
      <section className="lg:hidden py-8 px-4">
        <p className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-5 font-medium text-center">Supported Providers</p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {providers.map((provider) => (
            <div key={provider.name} className="glass shadow-3d rounded-xl px-4 py-2 flex items-center gap-2">
              <div className={`w-6 h-6 rounded-md bg-gradient-to-br ${provider.gradient} flex items-center justify-center`}>
                <span className="text-[8px] font-bold text-white">{provider.abbr}</span>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{provider.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-24 px-4">
        {/* Section background */}
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              Everything You Need in One Platform
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Built for individuals, teams, and organizations of any size, with the security and flexibility you require.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <TiltCard key={feature.title}>
                <div
                  className="relative group p-6 rounded-2xl glass shadow-3d overflow-hidden h-full"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Gradient accent line at top */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} text-white flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    style={{ transform: 'translateZ(20px)' }}
                  >
                    {feature.icon}
                  </div>
                  <h3
                    className="text-lg font-semibold text-gray-900 dark:text-white mb-2"
                    style={{ transform: 'translateZ(10px)' }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed"
                    style={{ transform: 'translateZ(5px)' }}
                  >
                    {feature.description}
                  </p>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
              Get Started in Minutes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Create an Account', description: 'Sign up with your email to get instant access to the platform.' },
              { step: '2', title: 'Choose Your Model', description: 'Select from GPT-4, Claude, Gemini, or any of our supported AI models.' },
              { step: '3', title: 'Start Chatting', description: 'Ask questions, upload files, compare models, and organize your work.' },
            ].map((item, index) => (
              <TiltCard key={item.step}>
                <div className="glass shadow-3d rounded-2xl p-8 text-center h-full">
                  <div
                    className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-center text-2xl font-bold mb-5 shadow-lg"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{item.description}</p>
                </div>
              </TiltCard>
            ))}
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 relative overflow-hidden">
        {/* 3D gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 dark:from-blue-800 dark:via-blue-900 dark:to-indigo-950" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-blue-100/80 mb-10">
            Access the world's leading language models from a single, powerful platform.
          </p>
          <Link
            to="/login"
            className="group inline-flex items-center gap-2 px-10 py-4 bg-white text-blue-700 font-semibold rounded-2xl hover:bg-blue-50 transition-all shadow-3d-lg"
          >
            Try Now
            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 dark:bg-black border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src={logoImg} alt="Logo" className="w-8 h-8" />
                <span className="text-lg font-semibold text-white">Unified LLM Platform</span>
              </div>
              <p className="text-gray-400 text-sm max-w-md leading-relaxed mb-4">
                One interface. Every major AI. Your conversations, your data, your control.
              </p>
              {/* AI slogans */}
              <div className="space-y-1">
                <p className="text-xs text-gray-600 italic">"The best model is the one that solves your problem."</p>
                <p className="text-xs text-gray-600 italic">"Intelligence amplified — yours and the machine's."</p>
              </div>
            </div>

            {/* Platform links */}
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Platform</h4>
              <ul className="space-y-2">
                <li><Link to="/login" className="text-sm text-gray-400 hover:text-white transition-colors">Sign In</Link></li>
                <li><Link to="/docs" className="text-sm text-gray-400 hover:text-white transition-colors">Documentation</Link></li>
                <li><Link to="/docs#faq" className="text-sm text-gray-400 hover:text-white transition-colors">FAQ</Link></li>
              </ul>
            </div>

            {/* Legal + Ko-fi */}
            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h4>
              <ul className="space-y-2 mb-6">
                <li><Link to="/imprint" className="text-sm text-gray-400 hover:text-white transition-colors">Imprint</Link></li>
                <li><Link to="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">Terms &amp; Conditions</Link></li>
              </ul>
              {/* Ko-fi */}
              <a
                href="https://ko-fi.com/imranhussain"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF5E5B] hover:bg-[#ff4744] text-white text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z"/>
                </svg>
                Buy me a coffee
              </a>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} IH Software Services Provider · Imran Hussain · All rights reserved.
            </p>
            <p className="text-sm text-gray-600 italic">
              "Not just one AI — all of them, unified."
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
